(() => {
  if (window.vrInstagramTheme) { window.vrInstagramTheme.refresh(); return; }
  const events = new AbortController();
  let current = null;
  let destroyed = false;
  let discovery = null;
  let discoveryDeadline = null;
  let frame = null;
  let resizeTimer = null;
  const motion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let userPaused = motion.matches;

  function stopAuto() {
    const auto = current?.auto;
    if (!auto) return;
    clearTimeout(auto.timer);
    auto.events.abort();
    auto.viewport?.disconnect();
    current.auto = null;
  }

  function setupAuto(feed, rail) {
    stopAuto();
    const button = feed.querySelector('.vr-instagram__auto');
    const next = feed.querySelector('.instagenie-slider-next');
    const previous = feed.querySelector('.instagenie-slider-prev');
    if (!button || !next || !previous) return;
    const auto = { events: new AbortController(), timer: null, visible: false, hovered: false, advancing: false };
    current.auto = auto;
    const { signal } = auto.events;
    button.hidden = false;
    rail.setAttribute('aria-live', 'off');
    const allowed = () => !destroyed && current?.auto === auto && feed.isConnected && auto.visible
      && !document.hidden && !userPaused && !auto.hovered
      && (!feed.contains(document.activeElement) || document.activeElement === button);
    const label = () => {
      button.setAttribute('aria-pressed', String(userPaused));
      button.setAttribute('aria-label', userPaused ? 'Reanudar fotos automáticas' : 'Pausar fotos automáticas');
    };
    const arm = () => {
      clearTimeout(auto.timer);
      auto.timer = null;
      if (!allowed()) return;
      auto.timer = setTimeout(() => {
        auto.timer = null;
        if (!allowed()) return;
        auto.advancing = true;
        // Use the provider's own navigation so its index, spacing and arrows agree.
        if (Number.parseFloat(getComputedStyle(next).opacity) <= 0.35) {
          for (let i = 0; i < rail.querySelectorAll('.instagenie-grid').length; i++) previous.click();
        } else next.click();
        auto.advancing = false;
        arm();
      }, 5000);
    };
    button.addEventListener('click', () => { userPaused = !userPaused; label(); arm(); }, { signal });
    feed.addEventListener('click', (event) => {
      if (!auto.advancing && event.target.closest('.instagenie-slider-prev,.instagenie-slider-next')) arm();
    }, { capture: true, signal });
    feed.addEventListener('pointerenter', (event) => { if (event.pointerType === 'mouse') { auto.hovered = true; arm(); } }, { signal });
    feed.addEventListener('pointerleave', () => { auto.hovered = false; arm(); }, { signal });
    feed.addEventListener('focusin', arm, { signal });
    feed.addEventListener('focusout', () => queueMicrotask(arm), { signal });
    document.addEventListener('visibilitychange', arm, { signal });
    motion.addEventListener?.('change', (event) => { if (event.matches) { userPaused = true; label(); arm(); } }, { signal });
    if (typeof IntersectionObserver !== 'undefined') {
      auto.viewport = new IntersectionObserver((entries) => {
        auto.visible = entries[0].isIntersecting && entries[0].intersectionRatio >= 0.15;
        arm();
      }, { threshold: [0, 0.15] });
      auto.viewport.observe(feed);
    }
    label();
  }

  function stopDiscovery() {
    discovery?.disconnect();
    discovery = null;
    clearTimeout(discoveryDeadline);
    discoveryDeadline = null;
  }

  function disconnectFeed() {
    stopAuto();
    current?.observer.disconnect();
    current?.resizeObserver?.disconnect();
    current = null;
  }

  function heading(inner) {
    if (inner.querySelector(':scope > .vr-instagram__heading')) return;
    const original = inner.querySelector(':scope > .h2');
    if (!original) return;
    const row = document.createElement('div');
    row.className = 'vr-instagram__heading';
    const title = document.createElement('h2');
    title.className = 'vr-instagram__title';
    title.textContent = '@vrposhop';
    const link = document.createElement('a');
    link.className = 'vr-instagram__link';
    link.href = 'https://www.instagram.com/vrposhop/';
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.append(document.createTextNode('Ver en Instagram'));
    const ns = 'http://www.w3.org/2000/svg';
    const icon = document.createElementNS(ns, 'svg');
    icon.setAttribute('viewBox', '0 0 24 16');
    icon.setAttribute('aria-hidden', 'true');
    icon.setAttribute('focusable', 'false');
    const path = document.createElementNS(ns, 'path');
    path.setAttribute('d', 'M1 8h21m-6-6 6 6-6 6');
    icon.append(path);
    link.append(icon);
    const actions = document.createElement('div');
    actions.className = 'vr-instagram__actions';
    const auto = document.createElement('button');
    auto.className = 'vr-instagram__auto';
    auto.type = 'button';
    auto.hidden = true;
    auto.setAttribute('aria-label', 'Pausar fotos automáticas');
    auto.setAttribute('aria-pressed', 'false');
    const autoIcon = document.createElementNS(ns, 'svg');
    autoIcon.setAttribute('viewBox', '0 0 24 24');
    autoIcon.setAttribute('aria-hidden', 'true');
    autoIcon.setAttribute('focusable', 'false');
    ['M9 6v12M15 6v12', 'm9 6 9 6-9 6Z'].forEach((d, index) => {
      const shape = document.createElementNS(ns, 'path');
      shape.setAttribute('d', d);
      shape.setAttribute('class', index ? 'vr-auto-play' : 'vr-auto-pause');
      autoIcon.append(shape);
    });
    auto.append(autoIcon);
    actions.append(auto, link);
    row.append(title, actions);
    original.replaceWith(row);
  }


  function spanishFeed(feed) {
    const phrases = [
      ['A glimpse of what’s coming.', 'Un adelanto de lo que viene.'],
      ['Are you ready for the next chapter?', '¿Estás lista para el próximo capítulo?'],
      ['The chapter is finally yours.', 'Este capítulo por fin es tuyo.'],
      ['The story continues', 'La historia continúa'],
      ['Welcome to ', 'Bienvenida a '],
      ['CHAPTER II', 'CAPÍTULO II'], ['Chapter II', 'Capítulo II'], ['Chapter I', 'Capítulo I'],
      ['best sellers', 'más vendidos'], ['best seller', 'más vendido'],
      ['PH & MAKEUP BY', 'Fotografía y maquillaje:'], ['instagram profile picture', 'Foto de perfil de Instagram'], ['off-shoulder', 'de hombros descubiertos'], ['off shoulder', 'de hombros descubiertos'], ['mesh', 'malla'], ['by VRSHOP', 'de VRSHOP'], ['close button', 'Cerrar'], ['View on Instagram', 'Ver en Instagram'], ['Powered by', 'Con tecnología de']
    ];
    const translate = value => phrases.reduce((text, pair) => text.split(pair[0]).join(pair[1]), value);
    const nodes = document.createTreeWalker(feed, NodeFilter.SHOW_TEXT, {
      acceptNode: node => node.parentElement.closest('script,style') ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT
    });
    while (nodes.nextNode()) {
      const node = nodes.currentNode, next = translate(node.nodeValue);
      if (next !== node.nodeValue) node.nodeValue = next;
    }
    feed.querySelectorAll('[alt],[aria-label],[title]').forEach(element => {
      ['alt', 'aria-label', 'title'].forEach(attribute => {
        if (!element.hasAttribute(attribute)) return;
        const original = element.getAttribute(attribute), next = translate(original);
        if (next !== original) element.setAttribute(attribute, next);
      });
    });
  }

  function apply() {
    const feed = document.getElementById('instagenie-feed');
    if (!feed) return false;
    spanishFeed(feed);
    if (current?.feed !== feed) {
      disconnectFeed();
      if (!feed.parentElement.classList.contains('vr-instagram')) {
        const wrapper = document.createElement('div');
        wrapper.className = 'vr-instagram';
        feed.before(wrapper);
        wrapper.append(feed);
      }
      current = { feed, rail: null, settings: null, signature: null,
        observer: new MutationObserver(schedule) };
      // Only app replacements directly under this feed are watched, not the whole page.
      current.observer.observe(feed, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['alt', 'aria-label', 'title'] });
      if (typeof ResizeObserver !== 'undefined') {
        current.resizeObserver = new ResizeObserver(schedule);
        current.resizeObserver.observe(feed);
      }
    }
    const inner = feed.querySelector(':scope > .instagenie-feed');
    if (inner) heading(inner);
    const rail = feed.querySelector('#instagenie-list');
    const widget = window.instaGenie;
    if (!rail || !widget?.settings || typeof widget.initSliderPlugin !== 'function') return false;
    if (widget.settings.widgetType !== 'SLIDER') return true;
    const viewport = feed.querySelector('.instagenie-slider-wrapper');
    const width = Math.round(feed.getBoundingClientRect().width);
    if (!viewport || width < 100 || !rail.querySelector('.instagenie-grid')) return false;
    const mobile = window.matchMedia('(max-width: 767px)').matches;
    const spacing = mobile ? 12 : 18;
    const signature = `${width}:${mobile}:${spacing}`;
    if (current.rail === rail && current.settings === widget.settings && current.signature === signature && current.failed) return true;
    if (current.rail === rail && current.settings === widget.settings && current.signature === signature
      && widget.settings.numImagesInRow === 4 && widget.settings.numImagesInRowMobile === 2
      && widget.settings.spacing === spacing && widget.is_mobile === mobile) return true;
    stopAuto();
    const autoButton = feed.querySelector('.vr-instagram__auto');
    if (autoButton) autoButton.hidden = true;
    // The observed widget has autoplay off. Reinitializing an autoplay version would
    // create vendor-owned intervals that cannot safely be cleaned up by this theme.
    if (widget.settings.autoPlay) {
      feed.dataset.vrInstagramLayout = 'provider-autoplay';
      return true;
    }
    const previous = {
      desktop: widget.settings.numImagesInRow,
      mobile: widget.settings.numImagesInRowMobile,
      spacing: widget.settings.spacing,
      isMobile: widget.is_mobile,
    };
    const oldArrows = Array.from(viewport.querySelectorAll('.instagenie-slider-prev, .instagenie-slider-next'));
    const oldStyles = [rail, viewport, ...rail.querySelectorAll('.instagenie-grid')].map((element) => [element, element.getAttribute('style')]);
    widget.settings.numImagesInRow = 4;
    widget.settings.numImagesInRowMobile = 2;
    widget.settings.spacing = spacing;
    widget.is_mobile = mobile;
    // The provider appends these properties when it recalculates. Clear just its
    // sizing properties so a real resize cannot accumulate duplicate declarations.
    rail.querySelectorAll('.instagenie-grid').forEach((card) => {
      ['width', 'margin-right', 'flex-shrink', 'list-style'].forEach((name) => card.style.removeProperty(name));
    });
    try {
      widget.initSliderPlugin();
      oldArrows.forEach((arrow) => arrow.remove());
      viewport.querySelectorAll('.instagenie-slider-prev, .instagenie-slider-next').forEach((button) => {
        button.type = 'button';
        button.setAttribute('aria-label', button.classList.contains('instagenie-slider-prev') ? 'Ver fotos anteriores' : 'Ver fotos siguientes');
      });
      current.failed = false;
      feed.dataset.vrInstagramLayout = 'ready';
    } catch (error) {
      widget.settings.numImagesInRow = previous.desktop;
      widget.settings.numImagesInRowMobile = previous.mobile;
      widget.settings.spacing = previous.spacing;
      widget.is_mobile = previous.isMobile;
      viewport.querySelectorAll('.instagenie-slider-prev, .instagenie-slider-next').forEach((arrow) => {
        if (!oldArrows.includes(arrow)) arrow.remove();
      });
      oldStyles.forEach(([element, style]) => {
        if (style === null) element.removeAttribute('style');
        else element.setAttribute('style', style);
      });
      current.failed = true;
      feed.dataset.vrInstagramLayout = 'unconfirmed';
    }
    current.rail = rail;
    current.settings = widget.settings;
    current.signature = signature;
    if (!current.failed) setupAuto(feed, rail);
    return true;
  }

  function schedule() {
    if (destroyed || frame !== null) return;
    frame = requestAnimationFrame(() => {
      frame = null;
      if (apply()) stopDiscovery();
    });
  }

  function refresh() {
    if (destroyed) return;
    if (apply()) { stopDiscovery(); return; }
    if (!discovery) {
      discovery = new MutationObserver(schedule);
      discovery.observe(document.body, { childList: true, subtree: true });
      // Discovery is bounded. Once present, only the feed's own replacements are observed.
      discoveryDeadline = setTimeout(stopDiscovery, 30000);
    }
  }

  const resize = () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(schedule, 160);
  };
  window.addEventListener('resize', resize, { signal: events.signal });
  document.addEventListener('shopify:section:load', refresh, { signal: events.signal });
  document.addEventListener('shopify:section:unload', (event) => {
    if (current && event.target.contains(current.feed)) disconnectFeed();
  }, { signal: events.signal });
  window.vrInstagramTheme = {
    refresh,
    destroy() {
      destroyed = true;
      events.abort();
      stopDiscovery();
      disconnectFeed();
      clearTimeout(resizeTimer);
      if (frame !== null) cancelAnimationFrame(frame);
      delete window.vrInstagramTheme;
    },
  };
  refresh();
})();
